import { earnedByCurrency, entryDuration, formatMoney, invoicedByCurrency, isLive, paidByCurrency, sortCurrencies, type Dataset, type Range } from "@otrack/domain";
import { Tooltip } from "@otrack/ui";
import { useApp, useDataset } from "./context";
import { formatDuration, formatMonth, HOUR, monthRange, startOfDay } from "./time";

/** Hours tracked on each day of the month, by entry start (the same rule as Today's totals). */
function dailyTracked(dataset: Dataset, month: Range, now: number): number[] {
  const days = Math.round((month.to - month.from) / 86_400_000);
  const totals = new Array<number>(days).fill(0);
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.start < month.from || entry.start >= month.to) continue;
    totals[new Date(entry.start).getDate() - 1]! += entryDuration(entry, now);
  }
  return totals;
}

const dayLabel = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

export function MonthStrip() {
  const { now } = useApp();
  const dataset = useDataset();
  const month = monthRange(now);
  const earned = earnedByCurrency(dataset, month, now);
  const invoiced = invoicedByCurrency(dataset, month);
  const paid = paidByCurrency(dataset, month);
  const goals = dataset.settings.monthlyGoals;
  const currencies = sortCurrencies([...Object.keys(earned), ...Object.keys(goals), ...Object.keys(invoiced), ...Object.keys(paid)]);
  const invoicing = Object.values(dataset.invoices).some((invoice) => isLive(invoice) && invoice.status !== "draft");
  const daily = dailyTracked(dataset, month, now);
  const today = new Date(now).getDate() - 1;
  const total = daily.reduce((sum, value) => sum + value, 0);
  const worked = daily.filter((value) => value > 0).length;
  // Scale to the busiest day, but never below a full 8-hour day so light months look light.
  const scale = Math.max(8 * HOUR, ...daily);

  return <section className="ot-month" aria-label="This month">
    <div className="ot-month__money">
      <h2>{formatMonth(now)} so far</h2>
      {currencies.length === 0 ? <p className="ot-month__empty">Earnings appear here once billable time is tracked.</p>
        : currencies.map((currency) => {
          const amount = earned[currency] ?? 0;
          const goal = goals[currency];
          const progress = goal ? Math.min(1, amount / goal) : 0;
          return <div className="ot-month__currency" key={currency}>
            <div className="ot-month__earned">{formatMoney({ amount, currency })}</div>
            <div className="ot-month__sub">Earned in {currency}</div>
            {invoicing && <dl className="ot-month__flow">
              <div><dt>Invoiced</dt><dd className="ot-mono">{formatMoney({ amount: invoiced[currency] ?? 0, currency })}</dd></div>
              <div><dt>Paid</dt><dd className="ot-mono">{formatMoney({ amount: paid[currency] ?? 0, currency })}</dd></div>
            </dl>}
            {goal ? <div className="ot-month__goal">
              <div className="ot-meter" role="meter" aria-valuemin={0} aria-valuemax={goal} aria-valuenow={amount} aria-label={`${currency} goal`}><i style={{ transform: `scaleX(${progress})` }}/></div>
              <span><strong className="ot-mono">{Math.round(progress * 100)}%</strong> of {formatMoney({ amount: goal, currency })}</span>
            </div> : null}
          </div>;
        })}
      {currencies.length > 1 && <p className="ot-month__note">Each currency is totalled on its own, never converted.</p>}
    </div>
    <div className="ot-month__days">
      <div className="ot-month__days-head">
        <span><strong className="ot-mono">{formatDuration(total)}</strong> tracked</span>
        <span><strong className="ot-mono">{worked}</strong> {worked === 1 ? "day" : "days"} worked</span>
      </div>
      <div className="ot-days" style={{ ["--ot-days" as string]: daily.length }} role="img" aria-label={`Hours tracked each day of ${formatMonth(now)}`}>
        <i className="ot-days__ref" style={{ bottom: `${8 * HOUR / scale * 100}%` }}><span className="ot-mono">8h</span></i>
        {daily.map((value, index) => {
          const date = startOfDay(month.from, index);
          const future = index > today;
          const label = `${dayLabel.format(date)}\n${future ? "Ahead" : value ? formatDuration(value) : "Nothing tracked"}`;
          return <Tooltip key={index} label={label} delay={120}>
            <span className={`ot-days__col ${index === today ? "is-today" : ""} ${future ? "is-future" : ""}`} style={{ ["--i" as string]: index }}>
              {!future && value > 0 && <i style={{ height: `${Math.max(3, value / scale * 100)}%` }}/>}
            </span>
          </Tooltip>;
        })}
      </div>
      <div className="ot-days__axis ot-mono" aria-hidden="true" style={{ ["--ot-days" as string]: daily.length }}>
        {daily.map((_, index) => <span key={index}>{index === today || (index % 7 === 0 && Math.abs(index - today) > 2) ? index + 1 : ""}</span>)}
      </div>
      <div className="ot-sr-only"><table>
        <caption>Hours tracked each day of {formatMonth(now)}</caption>
        <tbody>{daily.slice(0, today + 1).map((value, index) => <tr key={index}><th scope="row">{dayLabel.format(startOfDay(month.from, index))}</th><td>{formatDuration(value)}</td></tr>)}</tbody>
      </table></div>
    </div>
  </section>;
}
